import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { Textbox, useSimpleInputState } from "capitalrx-components-2.0/lib/forms";
import { Buttons, Color, LoadingGroup, LoadingSkeleton, Message, Messages, useAlert } from "capitalrx-components-2.0/lib/general";
import { Shelf, Stack, useResponsive } from "capitalrx-components-2.0/lib/layout";
import { Body, Semibold, Subheading } from "capitalrx-components-2.0/lib/typography";
import { formatDollars } from "capitalrx-utilities";
import Decimal from "decimal.js";

import { useGetMemberPaymentConfigurationQuery } from "@/App/api/medicarePrescriptionPaymentPlan";
import { ThemedButton, ThemedRadioGroup } from "@/App/components";
import { MemberEligibilityContext } from "@/App/context";
import { useIsLoggedInUser } from "@/App/hooks";
import { usePlanYear } from "@/App/pages";
import { useBalance } from "@/App/pages/MedicarePrescriptionPaymentPlan/Participation/useBalance";
import { getAllowedPaymentMethods, getCustomerSupportNumber } from "@/App/utils/utils";

import { StripePaymentElement, StripePaymentForm } from "../../Stripe/StripePaymentForm";

interface OneTimePaymentForm {
  readonly paymentAmount: "current_invoice_balance" | "current_accumulation_balance" | "other";
  readonly paymentMethod: "card" | "ach";
  readonly otherPaymentAmount: string | null;
  readonly otherPaymentDate: string;
}

const minimumPaymentAmount = new Decimal(0.5);
const TRANSLATION_PATH = "MEDICARE_PRESCRIPTION_PAYMENT_PLAN.makeAPaymentForm";

export const MakeAPaymentForm = () => {
  const [hasStripeLoadingError, setHasStripeLoadingError] = useState(false);
  const [isLoadingStripeForm, setIsLoadingStripeForm] = useState(false);
  const [isMakingAPayment, setIsMakingAPayment] = useState(false);
  const [customAmountError, setCustomAmountError] = useState(null);

  const { t: translate } = useTranslation();
  const planYear = usePlanYear();

  const { memberEligibility } = useContext(MemberEligibilityContext);
  const allowedPaymentMethods = getAllowedPaymentMethods(memberEligibility);
  const canPayNowOnline = allowedPaymentMethods.includes("ach") || allowedPaymentMethods.includes("card");

  const stripeFormRef = useRef<HTMLButtonElement>();
  const [method, setMethod] = useSimpleInputState<OneTimePaymentForm["paymentMethod"]>(
    allowedPaymentMethods.includes("card") ? "card" : "ach",
  );
  const [customAmount, setCustomAmount] = useSimpleInputState<OneTimePaymentForm["otherPaymentAmount"]>(null);
  const [paymentAmountStrategy, setPaymentAmountStrategy] =
    useSimpleInputState<OneTimePaymentForm["paymentAmount"]>("current_invoice_balance");
  useEffect(() => {
    if (paymentAmountStrategy !== "other") {
      const defaultAmount = invoiceBalance.gt(0) ? invoiceBalance.toString() : accumulationBalance.toString();
      setCustomAmount(defaultAmount, customAmount);
      setCustomAmountError(null);
    }
  }, [paymentAmountStrategy]);
  const { isBalanceLoading, isBalanceError, hasBalance, hasProcessingPayments, invoiceBalance, accumulationBalance } = useBalance(planYear);

  const {
    data: paymentConfiguration,
    isLoading: isLoadingPaymentConfiguration,
    isError: isPaymentConfigurationError,
  } = useGetMemberPaymentConfigurationQuery(planYear);
  const isPreferedPaymentMethodEligibleForOneTimePayment =
    !isLoadingPaymentConfiguration
    && !isPaymentConfigurationError
    && ["card", "ach"].includes(paymentConfiguration?.preferred_payment_method);

  const { isCompressed } = useResponsive({
    isCompressed: {
      mobile: true,
      desktop: false,
    },
  });

  const showPaymentMethodForm = () => {
    return (
      <>
        <Semibold>{translate(`${TRANSLATION_PATH}.selectAPaymentMethod`)}</Semibold>
        <ThemedRadioGroup testId="select-payment-method" hideLabel orientation="vertical" value={method} onChange={setMethod}>
          {allowedPaymentMethods.includes("card") && (
            <ThemedRadioGroup.Option value="card">
              <Body style={{ marginBottom: -20 }}>{translate(`${TRANSLATION_PATH}.card`)}</Body>
            </ThemedRadioGroup.Option>
          )}
          {allowedPaymentMethods.includes("ach") && (
            <ThemedRadioGroup.Option value="ach">
              <Body>{translate(`${TRANSLATION_PATH}.bankAccountAch`)}</Body>
            </ThemedRadioGroup.Option>
          )}
        </ThemedRadioGroup>
      </>
    );
  };

  const showPreferedPaymentMethod = () => {
    return (
      <>
        <Semibold>{translate(`${TRANSLATION_PATH}.paymentMethod`)}</Semibold>
        {paymentConfiguration?.preferred_payment_method === "card" && <Body>{translate(`${TRANSLATION_PATH}.card`)}</Body>}
        {paymentConfiguration?.preferred_payment_method === "ach" && <Body>{translate(`${TRANSLATION_PATH}.ACH`)}</Body>}
      </>
    );
  };

  const showStripeForm = () => {
    const paymentMethod = isPreferedPaymentMethodEligibleForOneTimePayment ? paymentConfiguration?.preferred_payment_method : method;
    const showPaymentMethodForm = !isPreferedPaymentMethodEligibleForOneTimePayment;
    return (
      <>
        {(paymentMethod === "card" || paymentMethod === "ach") && !hasStripeLoadingError && (
          <StripePaymentElement amount={amountToPay()} onError={onStripeError} paymentMethod={paymentMethod}>
            <StripePaymentForm
              amount={amountToPay()}
              paymentMethod={paymentMethod}
              submitRef={stripeFormRef}
              setLoading={setIsLoadingStripeForm}
              handleLoadingError={handleStripeLoadingError}
              handlePaymentSuccess={handleStripePaymentSuccess}
              showPaymentMethodForm={showPaymentMethodForm}
            />
          </StripePaymentElement>
        )}
        {(paymentMethod === "card" || paymentMethod === "ach") && hasStripeLoadingError && (
          <Message appearance={Messages.Error} visible={true}>
            {translate(`${TRANSLATION_PATH}.unableToLoadStripe`)} <a href={`tel:${customerSupportNumber}`}>{customerSupportNumber}</a>.
          </Message>
        )}
      </>
    );
  };

  const showPaymentMethodSection = () => {
    return (
      <Stack spacing="mini">
        {isPreferedPaymentMethodEligibleForOneTimePayment && showPreferedPaymentMethod()}
        {!isPreferedPaymentMethodEligibleForOneTimePayment && showPaymentMethodForm()}
        {showStripeForm()}
      </Stack>
    );
  };

  const showAlert = useAlert();

  const customerSupportNumber = getCustomerSupportNumber(memberEligibility);

  const { isLoggedInUser } = useIsLoggedInUser();

  const onStripeError = () => {
    showAlert({
      message: translate(`${TRANSLATION_PATH}.unableToContactPaymentVendor`),
      type: "error",
      style: "banner",
    });
  };

  const handleStripeLoadingError = () => {
    setHasStripeLoadingError(true);
  };

  const handleStripePaymentSuccess = async () => {
    showAlert({
      message: translate(`${TRANSLATION_PATH}.successfullySubmittedPayment`),
      type: "success",
      style: "toast",
    });
    setIsMakingAPayment(false);
  };

  useEffect(() => {
    if (invoiceBalance.gt(0)) {
      setCustomAmount(invoiceBalance.toString(), customAmount);
    } else if (accumulationBalance.gt(0)) {
      setCustomAmount(accumulationBalance.toString(), customAmount);
    }

    if (paymentAmountStrategy === "current_invoice_balance" && !invoiceBalance.gt(0)) {
      setPaymentAmountStrategy("current_accumulation_balance", paymentAmountStrategy);
    } else if (paymentAmountStrategy === "current_accumulation_balance" && !accumulationBalance.gt(0)) {
      setPaymentAmountStrategy("current_invoice_balance", paymentAmountStrategy);
    }
  }, [invoiceBalance, accumulationBalance]);

  const maximumPaymentAmount = useMemo(() => {
    return Decimal.max(accumulationBalance, invoiceBalance, 0);
  }, [accumulationBalance, invoiceBalance]);

  const handleSetCustomAmount = (amount: string, prevAmount: string) => {
    setCustomAmount(amount, prevAmount);

    let customAmount: Decimal;
    try {
      customAmount = new Decimal(amount || 0);
    } catch (err) {
      if (err.message.startsWith("[DecimalError]")) {
        setCustomAmountError(translate(`${TRANSLATION_PATH}.invalidPaymentAmount`));
        return;
      }
      throw err;
    }

    if (customAmount.lessThan(minimumPaymentAmount)) {
      setCustomAmountError(`${translate(`${TRANSLATION_PATH}.paymentCannotBeLessThan`)} ${minimumPaymentAmount}`);
      return;
    }
    if (customAmount.greaterThan(maximumPaymentAmount)) {
      if (invoiceBalance.greaterThan(accumulationBalance)) {
        setCustomAmountError(
          `${translate(`${TRANSLATION_PATH}.paymentCannotBeMoreThanInvoices`)} (${formatDollars(invoiceBalance.toString())})`,
        );
        return;
      } else {
        setCustomAmountError(
          `${translate(`${TRANSLATION_PATH}.paymentCannotBeMoreThanOutstandingBalance`)} (${formatDollars(accumulationBalance.toString())})`,
        );
        return;
      }
    }

    if (customAmount.decimalPlaces() > 2) {
      setCustomAmountError(translate(`${TRANSLATION_PATH}.paymentCannotHaveMoreThan2Decimal`));
      return;
    }

    setCustomAmountError(null);
  };

  const amountToPay = () => {
    if (paymentAmountStrategy === "current_invoice_balance" && invoiceBalance.gt(0)) {
      return invoiceBalance;
    }
    if (paymentAmountStrategy === "current_accumulation_balance" && accumulationBalance.gt(0)) {
      return accumulationBalance;
    }
    if (paymentAmountStrategy === "other") {
      if (customAmountError) {
        return invoiceBalance.gt(0) ? invoiceBalance : accumulationBalance;
      }
      return new Decimal(customAmount);
    }
    return invoiceBalance.gt(0) ? invoiceBalance : accumulationBalance;
  };

  const handleSubmitPayment = async () => {
    stripeFormRef?.current.click();
  };

  const StackOrShelf = isCompressed ? Stack : Shelf;

  return (
    <LoadingGroup loading={isBalanceLoading}>
      <LoadingGroup.Placeholder
        fallback={
          <Stack spacing="mini">
            <LoadingSkeleton style={{ width: "75px", height: "16px" }} />
            <LoadingSkeleton style={{ width: "175px", height: "14px" }} />
            <LoadingSkeleton style={{ width: "150px", height: "14px" }} />
          </Stack>
        }
      >
        {!isMakingAPayment && hasBalance && canPayNowOnline && (
          <StackOrShelf>
            <ThemedButton
              testId="make-a-payment"
              appearance={Buttons.Functional.Secondary.Large}
              disabled={
                !isLoggedInUser || isBalanceLoading || isBalanceError || maximumPaymentAmount.lessThan(0.5) || hasProcessingPayments
              }
              onClick={() => setIsMakingAPayment(true)}
            >
              {translate(`${TRANSLATION_PATH}.makeAPayment`)}
            </ThemedButton>
            {!isCompressed && <Shelf.Spacer />}
          </StackOrShelf>
        )}
        {isMakingAPayment && hasBalance && canPayNowOnline && (
          <Stack spacing="large">
            <Stack>
              <Subheading>{translate(`${TRANSLATION_PATH}.makeAPayment`)}</Subheading>
              <Body>{translate(`${TRANSLATION_PATH}.oneTimePaymentDescription`)}</Body>
            </Stack>

            <Stack spacing="mini">
              <Semibold>{translate(`${TRANSLATION_PATH}.paymentAmount`)}</Semibold>
              <ThemedRadioGroup testId="payment-amount-source" hideLabel value={paymentAmountStrategy} onChange={setPaymentAmountStrategy}>
                {invoiceBalance.gt(0) && (
                  <ThemedRadioGroup.Option value="current_invoice_balance">
                    <Body>{translate(`${TRANSLATION_PATH}.currentInvoiceBalance`)}</Body>
                    <Semibold>{formatDollars(invoiceBalance.toNumber())}</Semibold>
                  </ThemedRadioGroup.Option>
                )}
                {accumulationBalance.gt(0) && (
                  <ThemedRadioGroup.Option value="current_accumulation_balance">
                    <Body>{translate(`${TRANSLATION_PATH}.currentAccumulationBalance`)}</Body>
                    <Semibold>{formatDollars(accumulationBalance.toNumber())}</Semibold>
                  </ThemedRadioGroup.Option>
                )}
                <ThemedRadioGroup.Option value="other">
                  <Body style={{ marginBottom: "-20px" }}>{translate(`${TRANSLATION_PATH}.otherAmount`)}</Body>
                </ThemedRadioGroup.Option>
              </ThemedRadioGroup>
              {paymentAmountStrategy === "other" && (
                <>
                  <Textbox style={{ paddingLeft: "25px" }} prefix="$" hideLabel value={customAmount} onChange={handleSetCustomAmount} />
                  {customAmountError && (
                    <Body inline size="mini" style={{ color: Color.toString(Color.Red["600"]), paddingLeft: "25px" }}>
                      {customAmountError}
                    </Body>
                  )}
                </>
              )}
            </Stack>
            {showPaymentMethodSection()}

            <StackOrShelf spacing="small">
              <ThemedButton
                testId="make-a-payment"
                appearance={Buttons.Functional.Primary.Large}
                disabled={!isLoggedInUser || isLoadingStripeForm || customAmountError || isBalanceLoading || hasProcessingPayments}
                onClick={handleSubmitPayment}
              >
                {translate(`${TRANSLATION_PATH}.makeAPayment`)}
              </ThemedButton>
              <ThemedButton
                appearance={Buttons.Functional.Secondary.Large}
                disabled={!isLoggedInUser || isBalanceLoading}
                onClick={() => setIsMakingAPayment(false)}
              >
                {translate(`${TRANSLATION_PATH}.cancel`)}
              </ThemedButton>
              <Shelf.Spacer />
            </StackOrShelf>
          </Stack>
        )}
      </LoadingGroup.Placeholder>
    </LoadingGroup>
  );
};
